import { useEffect, useState } from 'react';

import useViewMode from '~/common/hooks/useViewMode.ts';
import { breakpoints } from '~/common/utils/breakpoint.utils';

import type { BreakpointKey } from '~/common/utils/breakpoint.utils';

/**
 * 화면이 해당 브레이크포인트 이상인지.
 *
 * @description 레이아웃을 폭으로 가르는 단일 판정 지점이다. **기기(User-Agent)로 가르지 않는다** —
 *  아이패드·폴더블·"데스크톱 사이트 요청"·창 줄이기를 UA 로는 전부 오판하고, 정적 호스팅이라
 *  서버가 응답을 갈라줄 수도 없다. 폭은 틀릴 일이 없고 창을 줄이면 즉시 따라온다.
 *
 *  CSS 미디어쿼리로 되는 일이면 CSS 로 한다. 이 훅은 **그릴 컴포넌트 자체가 달라질 때**만 쓴다
 *  (하단 탭 ↔ 상단 네비게이션처럼). 양쪽을 다 그려놓고 CSS 로 감추면 안 읽음 배지 같은 상태가
 *  두 벌 구독되고, 스크린리더에는 없는 네비게이션이 두 개로 읽힌다.
 */
function useMinWidth(key: BreakpointKey): boolean {
  const query = `(min-width: ${breakpoints[key]}px)`;
  const [viewMode] = useViewMode();

  // 첫 렌더부터 올바른 셸을 그린다 — false 로 시작하면 데스크톱에서 하단 탭이 한 프레임 스쳐 지나간다.
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    // 훅이 마운트되기 전에 폭이 바뀌었을 수 있다(초기값은 첫 렌더 시점 기준이다).
    setMatches(mediaQuery.matches);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [query]);

  /*
    사용자가 직접 고른 화면 모드가 있으면 폭 판정을 덮는다(푸터의 "PC 화면으로 보기").
    덮는 자리를 여기 한 곳으로 모아야 화면마다 다른 답이 나오지 않는다 — 페이지가 각자
    localStorage 를 읽으면 한 화면은 PC 로, 다른 화면은 모바일로 그려진다.
  */
  if (viewMode === 'desktop') return true;
  if (viewMode === 'mobile') return false;

  return matches;
}

export default useMinWidth;
